import GenreSection from "./home/GenreSection";
import ArtistCardCoursel from "./home/ArtistCardCoursel";
import { lazy, Suspense, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RouteName } from "constant/routeNames";
import MusicService from "services/music/MusicService";
import AlbumCoursel from "./home/AlbumCoursel";
import { useSelector } from "react-redux";
import PlaylistService from "services/music/PlaylistService";
import PlaylistCoursel from "./playlist/PlaylistCoursel";
import HeroSkeleton from "../../components/skeleton/HeroSekeleton";
import MostPlayedSongSkeleton from "../../components/skeleton/MostPlayedSongSkeleton";
import MusicCardCourselSkeleton from "../../components/skeleton/MusicCardCourselSkeleton";
import { Helmet } from "react-helmet-async";
import ErrorBoundary from "components/error-fallbacks/ErrorFallBack";
import SectionHeading from "components/shared/SectionHeading";

const MostPlayedSongList = lazy(() => import("./home/MostPlayedSongList"));
const HeroSlider = lazy(() => import("./home/HeroSlider"));
const MusicCardCoursel = lazy(() => import("./home/MusicCardCoursel"));

// consistent modern "See all" link
const SeeAll = ({ to }: { to: string }) => (
  <Link to={to} className="group shrink-0">
    <p className="flex items-center gap-1 text-sm font-bold text-[#F50537] transition-all duration-200 group-hover:gap-2">
      See all
      <span className="transition-transform duration-200 group-hover:translate-x-0.5">
        →
      </span>
    </p>
  </Link>
);


function MusicPageHome() {
  const loggedInUser = useSelector((state: any) => state?.user?.currentUser);
  const [latestSongs, setLatestSongs] = useState([]);
  const [mostPlayed, setMostPlayed] = useState([]);
  const [userPlaylists, setUserPlaylists] = useState([]);
  const [isLoading, setIsLoding] = useState<any>(false);
  const [isLoadingMusic, setIsLodingMusic] = useState<any>(false);
  const [isLoadingPlaylist, setIsLodingPlaylist] = useState<any>(false);
  const [publicPlaylists, setPublicPlaylists] = useState<any>([]);

  useEffect(() => {
    setIsLodingPlaylist(true);
    if (loggedInUser) {
      PlaylistService.getUserPlaylists().then((res) => {
        setIsLodingPlaylist(false);
        setUserPlaylists(res.data.data);
      });
    }
  }, [loggedInUser]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    setIsLoding(true);
    setIsLodingMusic(true);
    MusicService.getLatestMusic()
      .then((res: any) => {
        setIsLodingMusic(false);
        setLatestSongs(res.latestSongs);
      })
      .catch((e) => {
        setIsLodingMusic(false);
      });
    MusicService.getMostPlayed()
      .then((res: any) => {
        setIsLoding(false);
        setMostPlayed(res.mostPlayedSongs);
      })
      .catch((e) => {
        setIsLoding(false);
      });
    PlaylistService.getPublicPlaylists()
      .then((res: any) => {
        setIsLoding(false);
        if (loggedInUser) {
          const filteredData = res.data.data.filter(
            (item: any) => item?.user?.id !== loggedInUser?.id
          );
          setPublicPlaylists(filteredData);
        } else {
          setPublicPlaylists(res.data.data);
        }
      })
      .catch((e) => {
        setIsLoding(false);
      });
  }, []);

  const FE_URL = process.env.REACT_APP_FRONTEND_URL;

  return (
    <>
      <Helmet>
        <title>AYOZAT Music</title>
        <meta
          name="description"
          content="Discover Music on AYOZAT, including songs, albums, artists and playlists from a wide range of genres and styles."
        />
        <link rel="canonical" href={`${FE_URL}/music`} />
        <meta property="og:title" content="AYOZAT Music" />
        <meta
          property="og:description"
          content="Discover Music on AYOZAT, including songs, albums, artists and playlists from a wide range of genres and styles."
        />
        <meta property="og:image" content="https://ayozat.co.uk/logo192.png" />
        <meta property="og:url" content={`${FE_URL}/music`} />
        <meta property="og:type" content="website" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AYOZAT Music" />
        <meta
          name="twitter:description"
          content="Discover Music on AYOZAT, including songs, albums, artists and playlists from a wide range of genres and styles."
        />
        <meta name="twitter:image" content="https://ayozat.co.uk/logo192.png" />
        <meta name="twitter:site" content="AYOZAT Music" />
        <meta name="twitter:url" content={`${FE_URL}/music`} />
      </Helmet>

      <div className={`w-full gap-5 `}>
        {/* hero section */}
        <div className="flex max-sm:w-full max-sm:block max-lg:block max-xl:block max-xl:w-full">
          {!isLoading && (
            <ErrorBoundary>
              <Suspense fallback={<HeroSkeleton />}>
                <div className="w-[70%] max-sm:w-full max-md:w-full max-lg:w-full max-xl:w-full ">
                  <HeroSlider value={mostPlayed[0]} />
                </div>
              </Suspense>
            </ErrorBoundary>
          )}
          {isLoading && <HeroSkeleton />}
          <div className="px-4 w-[30%] max-sm:w-full max-sm:px-0  max-md:block max-lg:block max-lg:w-full max-lg:px-0 max-lg:mt-3 max-xl:w-full max-xl:block max-xl:px-0 max-xl:mt-3 ">
            {!isLoading && (
              <ErrorBoundary>
                <Suspense fallback={<MostPlayedSongSkeleton />}>
                  <MostPlayedSongList
                    audios={mostPlayed.slice(1, 7)}
                    user={loggedInUser}
                    userPlaylists={userPlaylists}
                    setUserPlaylists={setUserPlaylists}
                  />
                </Suspense>
              </ErrorBoundary>
            )}
            {isLoading && <MostPlayedSongSkeleton />}
          </div>
        </div>

        {/* New Release and genre */}
        <div className="flex justify-between mb-10 max-sm:w-full max-sm:block max-md:block">
          <div className="w-[68%] max-sm:w-full max-md:w-full">
            <div className="items-center justify-between mt-[40px] flex max-sm:mx-0 max-md:mt-[9%]">
              <SectionHeading text="Picked for you" />
              <SeeAll to={RouteName.SeeAllNewReleaseRoute} />
            </div>
            {!isLoadingMusic && (
              <ErrorBoundary>
                <Suspense fallback={<MusicCardCourselSkeleton />}>
                  <MusicCardCoursel audios={latestSongs} user={loggedInUser} />
                </Suspense>
              </ErrorBoundary>
            )}
            {isLoadingMusic && <MusicCardCourselSkeleton />}
            <div className="flex flex-row items-center justify-between mt-[40px] max-sm:mx-1 ">
              <SectionHeading text="Artists you might like" />
              <SeeAll to={RouteName.SeeAllArtistRoute + "?page=1"} />
            </div>
            <ArtistCardCoursel />
          </div>
          <div className=" mt-5 pl-5 pr-3 w-[30%] max-sm:w-full max-md:block max-lg:block max-lg:w-full max-md:pl-0 max-lg:pr-0">
            <GenreSection />
          </div>
        </div>

        {/* discover albums */}
        <div className="mb-10">
          <div className="flex flex-row items-center justify-between mt-5">
            <SectionHeading text="Discover Albums" />
            <SeeAll to={RouteName.SeeAllAlbumsRoute + "?page=1"} />
          </div>
          <AlbumCoursel />
        </div>

        {/* public playlists */}
        {publicPlaylists && publicPlaylists?.length > 0 && (
          <div className="mb-10">
            <div className="flex flex-row items-center justify-between mt-5">
              <SectionHeading text="Trending playlists today" />
              <SeeAll to={RouteName.SeeAllDiscoverPlaylistsRoute} />
            </div>
            <PlaylistCoursel
              playlists={publicPlaylists}
              isLoadingPlaylist={isLoadingPlaylist}
              isLoading={isLoading}
            />
          </div>
        )}

        {loggedInUser && userPlaylists && userPlaylists?.length > 0 && (
          <div className="mb-28">
            <div className="flex flex-row items-center justify-between mt-5">
              <SectionHeading text="Your Playlists" />
            </div>
            <PlaylistCoursel
              playlists={userPlaylists}
              isLoadingPlaylist={isLoadingPlaylist}
              isLoading={isLoading}
            />
          </div>
        )}
      </div>
    </>
  );
}

export default MusicPageHome;